import { Avatar, AvatarImage, AvatarFallback, AvatarInitials } from "@/components/Avatar";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { useMembersByIds } from "@/queries/organization-queries";
import { useMembership } from "@/hooks/use-membership";
import { useMemo, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { getProjectUsers } from "@/utils/projects";
import type { PlainProject } from "@/queries/project-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";

type AvatarStackPopoverUserListProps = {
    project: PlainProject;
};

export const AvatarStackPopoverUserList: FC<AvatarStackPopoverUserListProps> = ({ project }) => {
    const navigate = useNavigate();
    const { membership } = useMembership();

    // Collect user IDs from project creator and usedBy subjects
    const userIds = useMemo(() => {
        const ids: string[] = [];
        if (project.metadata?.creator?.id) ids.push(project.metadata.creator.id);
        for (const subject of project.usedBy?.subjects ?? []) {
            if (subject.id) ids.push(subject.id);
        }
        return ids;
    }, [project]);
    const { members: membersMap, isLoading: isLoadingMembers } = useMembersByIds(userIds);

    // Get full user list with avatars from org members
    const fullUserList = useMemo(() => {
        const membersArray = Array.from(membersMap.values());
        if (membersArray.length === 0) {
            return [];
        }

        const users = getProjectUsers(project, membersArray);
        if (!users) {
            return [];
        }

        return users.map((u) => ({
            id: u.userId,
            name: u.fullName,
            avatarUrl: u.avatarUrl,
        }));
    }, [membersMap, project]);

    const insightsLink = `/insights?project=${project.id}&selectedRange=last-14-days#most-active-users`;
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    return (
        <>
            <div className="max-h-64 overflow-y-auto p-2">
                {isLoadingMembers ? (
                    <div className="flex items-center justify-center py-8">
                        <IconSpinner size="base" className="animate-spin" />
                    </div>
                ) : (
                    <ul className="flex flex-col gap-2">
                        {fullUserList.map((user) => (
                            <li key={user.id} className="flex items-center gap-2 rounded p-1 hover:bg-surface-01">
                                <Avatar size={24}>
                                    {user.avatarUrl && (
                                        <AvatarImage src={user.avatarUrl} alt={`${user.name}'s avatar`} />
                                    )}
                                    <AvatarFallback>
                                        <AvatarInitials name={user.name} size={24} />
                                    </AvatarFallback>
                                </Avatar>
                                <span className="truncate text-sm">{user.name}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {isOrgAdmin && (
                <div className="flex flex-col gap-1 border-t border-border-light px-4 py-2">
                    <button
                        type="button"
                        className="text-accent text-left text-xs hover:underline"
                        onClick={() => navigate(insightsLink)}
                        data-tracking-id="insights-link-avatar-stack"
                    >
                        See all in Insights
                    </button>
                </div>
            )}
        </>
    );
};
